import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { SupportedLanguage, TranslationKeys, RTL_LANGUAGES } from '../i18n';
import { translations } from '../i18n/translations';
import { Cultures } from '../types';
import { useWidgetContext } from './WidgetContext';
import { BET_BUILDER_CHANGE_CULTURE,CHANGE_CULTURE } from '../utils/consts';

interface I18nContextState { 
  language: SupportedLanguage;
  translations: TranslationKeys;
  isRTL: boolean;
  
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextState | undefined>(undefined);

export interface I18nProviderProps {
  children: ReactNode;
  defaultLanguage?: SupportedLanguage;
  onLanguageChange?: (language: SupportedLanguage) => void;
}

const getNestedValue = (obj: any, path: string): string => {
  return path.split('.').reduce((current, key) => current?.[key], obj) || path;
};

/* Detect browser language
const detectBrowserLanguage = (): SupportedLanguage => {
  const browserLang = navigator.language.split('-')[0] as SupportedLanguage;
  return Object.keys(translations).includes(browserLang) ? browserLang : 'en';
};*/

export const I18nProvider: React.FC<I18nProviderProps> = ({
  children,
  onLanguageChange
}) => {
     
    const { config, dispatch } = useWidgetContext();
    const currentTranslations = translations[config.culture || 'en'];
    const isRTL = RTL_LANGUAGES.includes(config.culture || 'en');
    let language = config.culture || 'en';
    const handleCultureChange = (e: Event) => {
        const customEvent = e as CustomEvent<Cultures>;
        dispatch({ type: CHANGE_CULTURE,payload: customEvent.detail });
        language = customEvent.detail;
    };
    useEffect(() => {
        if (onLanguageChange) {
            onLanguageChange(config.culture || 'en');
        }
        document.addEventListener(BET_BUILDER_CHANGE_CULTURE, handleCultureChange);
        return () => {
          document.removeEventListener(BET_BUILDER_CHANGE_CULTURE, handleCultureChange);
        };
    }, [language, isRTL, onLanguageChange]);

    const t = (key: string): string => {
        return getNestedValue(currentTranslations, key);
    };

    return (
        <I18nContext.Provider value={{
          language,
          translations: currentTranslations,
          isRTL,
          t
          }}>
          {children}
        </I18nContext.Provider>
    );
};

export const useI18n = (): I18nContextState => {
    const context = useContext(I18nContext);
    if (!context) {
        throw new Error('useI18n must be used within an I18nProvider');
    }
    return context;
};

export default I18nContext;